import React, { useCallback, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';

import {
  ScenarioObligation,
  ScenariosObligationsListLookupData,
  useScenarioMetadata,
  useScenariosObligationsList,
  useScenariosObligationsListLookupData,
} from '@queries/scenarios';
import {
  useAddScenarioObligation,
  useDeleteScenarioObligation,
  useUpdateScenarioObligation,
} from '../../../shared/mutations/scenarios';

import { DeleteModalData } from '../scenarioManageMembers/types';

import EditableGridButtonsComponent from '../../../shared/gridEdit/EditableGridButtons.component';
import AgGrid from '../../../shared/agGrid/agGrid.component';
import NavigationPrompt from '../../../shared/navigationPrompt.component';
import ConfirmDialog from '../../../shared/confirmDialog.component';
import {
  GridMode,
  isEditingMode,
  useEditableGridLogic,
  UseEditableGridLogicParams,
} from '../../../shared/gridEdit/useEditableGridLogic';
import { useDialogState } from '../../../shared/hooks/useDialogState';
import agGridConfig from '../../../agGridConfig';
import { getColumnDefinitions } from './getColumnDefinitions';
import { validateScenarioObligation } from './validators';
import DeleteScenarioObligation from './DeleteScenarioObligationModal.component';
import { getScenarioObligationUniqueId } from './utils';
import { YesNoFlag } from '../../../shared/types';

type EditableGridParams = UseEditableGridLogicParams<ScenarioObligation, ScenariosObligationsListLookupData>;
type OnSaveCallback = EditableGridParams['onSave'];
type OnCancelCallback = EditableGridParams['onCancel'];

type ScenarioManagerObligationsGridProps = {
  scenarioId: number,
  clientId: number,
  companyCode?: string,
  selectRow: typeof selectRow,
  selectRowByRowIndex: typeof selectRowByRowIndex,
  deleteModalData: DeleteModalData<ScenarioObligation>,
  hideDeleteScenarioObligationModal: () => void,
  navigateToScenario: () => void,
}

const createObligationRowFactory = ({ periodName }: { periodName: string }) =>
  (): ScenarioObligation => ({
    periodName,
    companyCode: '',
    countryCode: '',
    countryTaxType: '',
    overrideTaxCurrency: '',
    federalFTCLimit: null,
    overrideTaxRate: null,
    overrideDefTaxRate: null,
    NOL: null,
    recordVA: null,
    stateApportionmentRate: null,
    stateMinimumTax: null,
    statePrePostNOL: null,
  });

const ScenarioManagerObligationsGrid = ({
  scenarioId,
  clientId,
  companyCode,
  selectRow,
  selectRowByRowIndex,
  deleteModalData,
  hideDeleteScenarioObligationModal,
}: ScenarioManagerObligationsGridProps) => {
  const [mode, setMode] = useState(GridMode.View);
  const [isResetting, setResetting] = useState(false);

  const {
    showDialog: showCancelDialog,
    hideDialog: hideCancelDialog,
    isDialogVisible: isCancelDialogVisible,
  } = useDialogState();

  const {
    data: scenarioMetadata,
  } = useScenarioMetadata({ scenarioId: Number(scenarioId) });

  const isScenarioLocked = scenarioMetadata?.lockedFlag === YesNoFlag.Y;

  const {
    data: scenarioObligations,
    isFetching: isFetchingScenarioObligations,
  } = useScenariosObligationsList({ scenarioId });

  const {
    lookUpData,
    isLoading: isLoadingLookupData,
  } = useScenariosObligationsListLookupData({ scenarioId });

  const onSaveCallback = useCallback((__, { scenarioObligation, resetEditableGrid }) => {
    setMode(GridMode.View);
    selectRow({
      name: agGridConfig.scenariosManageObligations.name,
      fields: agGridConfig.scenariosManageObligations.fields,
      data: scenarioObligation,
    });
    resetEditableGrid();
  }, [setMode, selectRow]);

  const {
    mutate: addScenarioObligation,
    isLoading: isAddingScenarioObligation,
  } = useAddScenarioObligation({ scenarioId }, onSaveCallback);

  const {
    mutate: updateScenarioObligation,
    isLoading: isUpdatingScenarioObligation,
  } = useUpdateScenarioObligation({ scenarioId }, onSaveCallback);


  const afterDelete = useCallback(() => {
    if (deleteModalData?.data) {
      selectRowByRowIndex(deleteModalData.data.rowIndex === 0 ? 0 : deleteModalData.data.rowIndex - 1);
    }
    hideDeleteScenarioObligationModal();
  }, [hideDeleteScenarioObligationModal, selectRowByRowIndex, deleteModalData]);

  const {
    mutate: deleteScenarioObligation,
    isLoading: isDeletingScenarioObligation,
  } = useDeleteScenarioObligation({ scenarioId }, afterDelete);

  const isLoading = isFetchingScenarioObligations
    || isLoadingLookupData
    || isAddingScenarioObligation
    || isUpdatingScenarioObligation
    || isResetting
    || isDeletingScenarioObligation;

  const shouldShowNavigationPrompt = isEditingMode(mode);

  const onSave = useCallback<OnSaveCallback>( async ({ hasErrors, addedRows, updatedRows, reset }) => {
    if (hasErrors) {
      return;
    }

    const [addedRow] = addedRows;
    const [updatedRow] = updatedRows;

    if (mode === GridMode.Add && addedRow) {
      setMode(GridMode.View);
      addScenarioObligation({
        scenarioObligation: addedRow,
        resetEditableGrid: reset,
      });
      return;
    }

    if (mode === GridMode.Edit && updatedRow) {
      setMode(GridMode.View);
      updateScenarioObligation({
        scenarioObligation: updatedRow,
        resetEditableGrid: reset,
      });
    }
  }, [addScenarioObligation, updateScenarioObligation, mode, setMode]);

  const onCancel = useCallback<OnCancelCallback>((hasChanges, reset) => {
    if (hasChanges) {
      showCancelDialog();
    } else {
      setMode(GridMode.View);
      reset();
    }
  }, [setMode, showCancelDialog]);

  const onReset = useCallback(() => {
    setResetting(false);
  }, [setResetting]);

  const {
    save,
    cancel,
    reset,
    addRow,
    setRowData,
    setEditedRow,
    editedRowId,
    gridApi,
    ...gridProps
  } = useEditableGridLogic<ScenarioObligation, ScenariosObligationsListLookupData>({
    data: scenarioObligations as ScenarioObligation[],
    onSave,
    onCancel,
    onReset,
    mode,
    createRow: createObligationRowFactory({
      periodName: lookUpData.periodName || '',
    }),
    validateRow: validateScenarioObligation,
    getRowUniqueId: getScenarioObligationUniqueId,
    includeMetaWhenSaving: false,
    clientId,
    context: lookUpData,
  });

  const onFirstDataRendered = () => {
    if (companyCode) {
      gridApi.setFilterModel({
        companyCode: {
          filterType: 'set',
          values: [companyCode],
        },
      });
    }
  };

  const editRow = useCallback(data => {
    setMode(GridMode.Edit);
    setEditedRow(data);
  }, [setEditedRow]);

  const onAddClick = useCallback(() => {
    setMode(GridMode.Add);
    addRow();
  }, [setMode, addRow]);

  const resetGrid = useCallback(() => {
    setResetting(true);
    reset();
    setMode(GridMode.View);
  }, [setResetting, reset, setMode]);

  const confirmCancel = useCallback(() => {
    setResetting(true);
    resetGrid();
    hideCancelDialog();
    setMode(GridMode.View);
  }, [resetGrid, hideCancelDialog, setMode, setResetting]);

  const columnDefinitions = useMemo(() => getColumnDefinitions({
    setRowData,
    editRow,
    lookUpData,
    context: {
      editedRowId,
      mode,
      isScenarioLocked,
    },
  }), [setRowData, editRow, lookUpData, editedRowId, mode, isScenarioLocked]);

  return (
    <div>
      <EditableGridButtonsComponent
        mode={mode}
        disabled={isLoading || isScenarioLocked}
        onAddClick={onAddClick}
        onCancelClick={cancel}
        onSaveClick={save}
      />
      <div className="ag-grid-full-page-with-secondary-menu">
        <AgGrid
          className="ag-theme-bootstrap"
          columnDefs={columnDefinitions}
          isGridLoading={isLoading}
          context={{
            ...lookUpData,
            editedRowId,
            mode,
          }}
          suppressRowClickSelection
          suppressScrollOnNewData
          enableBrowserTooltips
          stopEditingWhenCellsLoseFocus
          suppressCellFocus
          onFirstDataRendered={onFirstDataRendered} // eslint-disable-line react/jsx-no-bind
          rowSelection="multiple"
          withSearchBar
          {...gridProps}
        />
      </div>
      <NavigationPrompt show={shouldShowNavigationPrompt} />
      <ReactTooltip place="right" effect="float" />
      <ConfirmDialog
        onCancel={hideCancelDialog}
        onConfirm={confirmCancel}
        message="Are you sure you want to discard the changes?"
        title="Confirm Cancel"
        dismissText="No"
        submitText="Yes"
        isVisible={isCancelDialogVisible}
      />
      <DeleteScenarioObligation
        entityToRemove={deleteModalData?.data?.entityToRemove}
        rowIndex={deleteModalData?.data?.rowIndex}
        isVisible={deleteModalData.show}
        hideModal={hideDeleteScenarioObligationModal}
        remove={deleteScenarioObligation}
        isLoading={isDeletingScenarioObligation}
      />
    </div>
  );
};

export default ScenarioManagerObligationsGrid;
